import React__default from 'react';
import { determineDataTestIdPrefix, getBaseProps, getAriaProps } from '../../../helpers/propHelpers.js';
import classNames from 'classnames';
import { Button } from '../Button/Button.js';
import { toSignedMoneyString } from '../../../helpers/formatters.js';
import { round } from '../../../helpers/calculators.js';
import { DescriptionList } from '../DescriptionList/DescriptionList.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { ProgressBar } from '../ProgressBar/ProgressBar.js';

var DrawHealth;
(function (DrawHealth) {
    DrawHealth["PENDING"] = "pending";
    DrawHealth["POSITIVE"] = "positive";
    DrawHealth["WARNING"] = "warning";
    DrawHealth["NOT_VISIBLE"] = "hidden";
})(DrawHealth || (DrawHealth = {}));
const DrawCard = (props) => {
    const dataTestId = determineDataTestIdPrefix(props, 'built-components-draw-card');
    const getDrawHealthClass = (drawHealthStatus) => {
        // Default Card Status
        let drawCardHealthClass = 'DrawCard--positive';
        // If a new status has been defined, set the draw health card status
        if (drawHealthStatus) {
            drawCardHealthClass = `DrawCard--${drawHealthStatus}`;
        }
        return drawCardHealthClass;
    };
    const renderDrawMessages = (messages) => {
        return (React__default.createElement("ul", { className: "DrawCard__message-list", "data-testid": `${dataTestId}-messages` }, messages.map((message, index) => {
            return (React__default.createElement("li", { className: "DrawCard__message", key: index, "data-testid": `${dataTestId}-message-${index}` }, message));
        })));
    };
    const renderChecklist = (numItemsCompleted, numItemsTotal, statusText) => {
        const percentComplete = round((numItemsCompleted / numItemsTotal) * 100);
        return (React__default.createElement("div", { className: "DrawCard__checklist", "data-testid": `${dataTestId}-checklist` },
            React__default.createElement(ProgressBar, { data: [{ value: percentComplete }] }),
            React__default.createElement("div", { className: "DrawCard__checklist-details" },
                React__default.createElement("span", null, statusText),
                React__default.createElement("span", null, percentComplete + '%'))));
    };
    const renderApprovalsList = (drawApprovals, noApprovalsText) => {
        return (React__default.createElement("div", { className: "DrawCard__approvals-list" }, drawApprovals.approvalsList.length ? (drawApprovals.approvalsList.map((approval, index) => {
            return (React__default.createElement("div", { className: "DrawCard__approvals-row-wrapper", key: index },
                React__default.createElement(Tooltip, { headline: approval.tooltip.headline, headlineIcon: { icon: approval.tooltip.headlineIcon }, description: approval.tooltip.description, position: "top-start" },
                    React__default.createElement("div", { className: "DrawCard__approvals-row" },
                        React__default.createElement("div", { className: "DrawCard__approval-icon-container" }, approval.isApproved ? (React__default.createElement(FontAwesomeIcon, { icon: "check-circle", className: "DrawCard__approval-icon DrawCard__approval-icon--approved" })) : (React__default.createElement(FontAwesomeIcon, { icon: "clock", className: "DrawCard__approval-icon" }))),
                        approval.approverFullName))));
        })) : (React__default.createElement("div", { className: "DrawCard__empty-text" }, noApprovalsText))));
    };
    const getListItems = () => {
        const listItems = [];
        if (props.cardData.drawAmount) {
            listItems.push({
                label: props.cardData.drawAmount.label,
                customValueComponent: (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement("h2", { "data-testid": `${dataTestId}-amount`, className: "DrawCard__amount" }, toSignedMoneyString(props.cardData.drawAmount.value)),
                    props.drawMessages && !props.suppressDrawMessages && renderDrawMessages(props.drawMessages || []))),
            });
        }
        if (props.cardData.requestedBy) {
            listItems.push({
                label: props.cardData.requestedBy.label,
                value: props.cardData.requestedBy.value,
            });
        }
        if (props.cardData.drawAvailability) {
            listItems.push({
                label: props.cardData.drawAvailability.label,
                value: toSignedMoneyString(props.cardData.drawAvailability.value),
            });
        }
        if (props.cardData.drawChecklist) {
            listItems.push({
                label: props.cardData.drawChecklist.label,
                value: renderChecklist(props.cardData.drawChecklist.numItemsCompleted, props.cardData.drawChecklist.numItemsTotal, props.cardData.drawChecklist.statusText),
            });
        }
        if (props.cardData.drawApprovals) {
            listItems.push({
                label: 'Approvals',
                customValueComponent: renderApprovalsList(props.cardData.drawApprovals, props.noApprovalsText),
            });
        }
        return listItems;
    };
    const baseProps = getBaseProps(props);
    getAriaProps(props);
    return (React__default.createElement("div", Object.assign({}, baseProps, { "data-testid": `${dataTestId}`, className: classNames('DrawCard', getDrawHealthClass(props.drawHealthStatus), props === null || props === void 0 ? void 0 : props.className), style: props === null || props === void 0 ? void 0 : props.style }), props.variant === 'compact' && (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("header", { className: "DrawCard__header" },
            React__default.createElement("h3", { className: "DrawCard__title", "data-testid": `${dataTestId}-title` },
                props.cardTitle,
                React__default.createElement("span", { className: "DrawCard__date" }, props.cardData.formattedRequestDate))),
        React__default.createElement("section", { className: "DrawCard__body" },
            !props.suppressDrawTicket && (React__default.createElement(Button, { type: "secondary", size: "tiny", iconLeft: { icon: 'ticket-alt' }, className: "DrawCard__ticket", handleClick: props === null || props === void 0 ? void 0 : props.handleTicketClick }, props.drawTicketButtonText)),
            React__default.createElement(DescriptionList, { listItems: getListItems() })),
        !props.suppressManageDraw && (React__default.createElement("footer", { className: "DrawCard__footer" },
            React__default.createElement(Button, { type: "secondary", iconLeft: { icon: 'pen' }, isFullWidth: true, handleClick: props === null || props === void 0 ? void 0 : props.handleManageDrawClick }, props.manageDrawButtonText)))))));
};
DrawCard.defaultProps = {
    variant: 'compact',
};

export { DrawCard, DrawHealth, DrawCard as default };
//# sourceMappingURL=DrawCard.js.map
